import { Button, Group, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { match, P } from "ts-pattern";
import { routes } from "@/configs/router.config/routes";
import { Workspace } from "@/models/workspace";

interface Props {
  submit: (name: string, categoryMaxLevel: number) => Promise<void>;
  isLoading: boolean;
  isUpdate?: boolean;
  workspace?: Workspace;
}

interface Form {
  name: string;
  categoryMaxLevel: string;
}

const WorkspaceEditForm: React.FC<Props> = (
  { submit, isLoading, isUpdate, workspace },
) => {
  const form = useForm<Form>({
    initialValues: {
      name: workspace?.name ?? "",
      categoryMaxLevel: workspace ? String(workspace.categoryMaxLevel - 1) : "2",
    },
    validate: {
      name: (value) =>
        match(value.length).with(0, () => "ワークスペース名を入力してください。").with(
          P.when((v) => v > 30),
          () => "30文字以内で入力してください。",
        ).otherwise(() => null),
    },
  });

  // 初期値をセット
  useEffect(() => {
    form.setValues({
      name: workspace?.name ?? "",
      categoryMaxLevel: workspace?.categoryMaxLevel ? String(workspace.categoryMaxLevel - 1) : "2",
    });
  }, [workspace?.name, workspace?.categoryMaxLevel]);

  const handleClickSubmit = async () => {
    const result = form.validate();
    if (result.hasErrors) return;

    await submit(form.values.name, Number(form.values.categoryMaxLevel) + 1);
  };

  return (
    <form>
      <Stack gap={20}>
        <TextInput
          data-testid="name-input"
          label="ワークスペース名"
          key={form.key("name")}
          w={300}
          required
          {...form.getInputProps("name")}
        />
        <Select
          data-testid="category-max-level-select"
          label="カテゴリ階層数"
          disabled={isUpdate}
          allowDeselect={false}
          w={200}
          maxDropdownHeight={200}
          style={{ width: "10rem" }}
          data={[1, 2, 3, 4].map((level) => level.toString())}
          {...form.getInputProps("categoryMaxLevel")}
        />
      </Stack>

      <Group>
        <Link to={routes.workspaceList}>
          <Button
            mt="25px"
            variant="outline"
            color="gray"
          >
            キャンセル
          </Button>
        </Link>

        <Button
          data-testid="submit-button"
          disabled={isLoading}
          onClick={handleClickSubmit}
          mt="25px"
        >
          {isUpdate ? "更新する" : "作成する"}
        </Button>
      </Group>
    </form>
  );
};

export default WorkspaceEditForm;
